/**
 * Pagination Hook
 * 
 * Manages page navigation state and handlers.
 */

import { useState, useCallback } from 'react';

/**
 * Custom hook for pagination logic
 * 
 * @param {number} totalPages - Total number of pages
 * @returns {object} Pagination state and handlers
 */
export function usePagination(totalPages) {
  const [currentPage, setCurrentPage] = useState(1);
  
  /**
   * Navigate to next page
   */
  const goToNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  }, [totalPages]);
  
  /**
   * Navigate to previous page
   */
  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);
  
  /**
   * Jump to specific page
   */
  const goToPage = useCallback((pageNumber) => {
    const page = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage(page);
  }, [totalPages]);
  
  /**
   * Reset to first page
   */
  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);
  
  const canGoNext = currentPage < totalPages;
  const canGoPrevious = currentPage > 1;
  
  return {
    currentPage,
    totalPages,
    canGoNext,
    canGoPrevious,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    resetToFirstPage,
  };
}


